import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { RecoveryFormatPlanRow } from '@shared/admin-recovery-types';

interface RecoveryFormatQuantitiesTableProps {
  formats: RecoveryFormatPlanRow[];
  values: Record<number, number>;
  discrepancyReasons: Record<number, string>;
  onChange: (formatId: number, value: number) => void;
  onDiscrepancyReasonChange: (formatId: number, reason: string) => void;
  deliveredLabel: string;
  showOperatorColumn?: boolean;
}

export function RecoveryFormatQuantitiesTable({
  formats,
  values,
  discrepancyReasons,
  onChange,
  onDiscrepancyReasonChange,
  deliveredLabel,
  showOperatorColumn = false,
}: RecoveryFormatQuantitiesTableProps) {
  const hasOperatorValues = showOperatorColumn && formats.some((row) => row.operatorDelivered != null);
  const plannedTotal = formats.reduce((sum, row) => sum + row.planned, 0);
  const deliveredTotal = formats.reduce((sum, row) => sum + (values[row.formatId] ?? row.planned), 0);
  const hasAnyDiscrepancy = formats.some((row) => (values[row.formatId] ?? row.planned) !== row.planned);

  return (
    <div className="space-y-2">
      <div className="rounded-lg border bg-muted/40 p-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left font-medium pb-1">Format</th>
              <th className="text-right font-medium pb-1 w-16">Planned</th>
              {hasOperatorValues && (
                <th className="text-right font-medium pb-1 w-16">Operator</th>
              )}
              <th className="text-right font-medium pb-1 w-24">{deliveredLabel}</th>
            </tr>
          </thead>
          <tbody>
            {formats.map((row) => {
              const current = values[row.formatId] ?? row.planned;
              const hasDiscrepancy = current !== row.planned;
              return (
                <React.Fragment key={row.formatId}>
                  <tr className="border-t">
                    <td className="py-1.5 font-medium pr-2">
                      {row.formatName}
                      {hasDiscrepancy && (
                        <Badge variant="destructive" className="ml-1.5 text-[10px] px-1 py-0">
                          Discrepancy
                        </Badge>
                      )}
                    </td>
                    <td className="py-1.5 text-right">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs px-1.5">
                        {row.planned}
                      </Badge>
                    </td>
                    {hasOperatorValues && (
                      <td className="py-1.5 text-right">
                        {row.operatorDelivered != null ? (
                          <Badge variant="outline" className="bg-sky-50 text-sky-700 text-xs px-1.5">
                            {row.operatorDelivered}
                          </Badge>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                    <td className="py-1.5 text-right">
                      <Input
                        type="number"
                        min={0}
                        className={`h-7 w-20 text-xs text-right ml-auto ${
                          hasDiscrepancy ? 'border-amber-500' : ''
                        }`}
                        value={current}
                        onChange={(e) =>
                          onChange(row.formatId, Math.max(0, parseInt(e.target.value, 10) || 0))
                        }
                        aria-label={`${deliveredLabel} for ${row.formatName}`}
                      />
                    </td>
                  </tr>
                  {hasDiscrepancy && (
                    <tr className="border-t bg-amber-50/50">
                      <td colSpan={hasOperatorValues ? 4 : 3} className="py-2 px-1">
                        <label className="text-[11px] font-medium text-amber-900 block mb-1">
                          Discrepancy reason (required)
                        </label>
                        <Textarea
                          className="min-h-[52px] text-xs bg-white"
                          placeholder={`Why does ${deliveredLabel.toLowerCase()} differ from planned for ${row.formatName}?`}
                          value={discrepancyReasons[row.formatId] ?? ''}
                          onChange={(e) => onDiscrepancyReasonChange(row.formatId, e.target.value)}
                          rows={2}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr className="border-t font-medium">
              <td className="py-1.5">Total</td>
              <td className="py-1.5 text-right">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs px-1.5">
                  {plannedTotal}
                </Badge>
              </td>
              {hasOperatorValues && (
                <td className="py-1.5 text-right">
                  <Badge variant="outline" className="bg-sky-50 text-sky-700 text-xs px-1.5">
                    {formats.reduce(
                      (sum, row) => sum + (row.operatorDelivered ?? 0),
                      0,
                    )}
                  </Badge>
                </td>
              )}
              <td className="py-1.5 text-right">
                <Badge
                  variant="outline"
                  className={`text-xs px-1.5 ${
                    deliveredTotal === plannedTotal
                      ? 'bg-green-50 text-green-700'
                      : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {deliveredTotal}
                </Badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {hasAnyDiscrepancy && (
        <p className="text-xs text-amber-800">
          Quantities differ from planned. Provide a reason for each format with a discrepancy before
          submitting.
        </p>
      )}
    </div>
  );
}
